import { useTranslation } from 'react-i18next';
import {
  ApiErrorText,
  Button,
  Card,
  CopyField,
  Loader,
} from '@/shared/components';
import { type InvoiceParam } from '../api';
import PrintIcon from '@/shared/assets/print-icon.svg?react';
import DownloadIcon from '@/shared/assets/download-icon.svg?react';

interface InvoiceDetailsContentProps {
  invoiceDetails: InvoiceParam[] | undefined;
  isError: boolean;
  isLoading: boolean;
  error: unknown;
  fileName: string;
  fileSizeStr: string;
  onDownload: () => void;
  onPrint: () => void;
  ConfirmationModal: React.ReactNode;
}

export function InvoiceDetailsContent({
  invoiceDetails,
  isError,
  isLoading,
  error,
  fileName,
  fileSizeStr,
  onDownload,
  onPrint,
  ConfirmationModal,
}: InvoiceDetailsContentProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[4rem]">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return <ApiErrorText error={error} />;
  }

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <div className="flex items-center justify-between p-[1.6rem] bg-background rounded-[1.6rem] shadow-sm border gap-[1.6rem]">
        <div className="flex flex-col overflow-hidden">
          <h3 className="headline4 text-foreground">{fileName}</h3>
          <p className="p5 text-muted-foreground">{fileSizeStr}</p>
        </div>
        <div className="flex items-center">
          <Button
            size="icon-button"
            variant="icon"
            onClick={onPrint}
            className="hidden sm:inline-flex"
            aria-label={t('common.print')}>
            <PrintIcon className="size-[2.4rem]" />
          </Button>
          <Button
            size="icon-button"
            variant="icon"
            onClick={onDownload}
            aria-label={t('common.download')}>
            <DownloadIcon className="size-[2.4rem]" />
          </Button>
        </div>
      </div>

      <Card variant="details" className="gap-[2.4rem]">
        {invoiceDetails &&
          invoiceDetails.map(p => {
            if (!p.isVisible) return null;

            return (
              <div className="flex flex-col gap-[0.2rem]" key={p.paramId}>
                <label className="p4 text-muted-foreground">
                  {p.paramName}
                </label>
                <CopyField value={p.paramValue} variant="compact" />
              </div>
            );
          })}
      </Card>

      {ConfirmationModal}
    </div>
  );
}
